import { Component, Input, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import Project from 'models/project/project.model';
import PaginatedList from 'models/paginated-list/paginated-list.model';
import Ticket from 'models/ticket/ticket.model';
import ProjectPermission from 'models/project-permission/project-permission.model';
import User from 'models/user/user.model';
import TicketStatus from 'models/ticket-status/ticket-status.model';
import TicketAttribute from 'models/ticket-attribute/ticket-attribute.model';
import { TicketAttributeCardComponent } from 'modules/ticket/ticket-attribute-card/ticket-attribute-card.component';
import { TicketService } from 'services/ticket/ticket.service';
import { ProjectService } from 'services/project/project.service';
import { ArcgisMapService } from 'services/arcgis-map/arcgis-map.service';
import { UserService } from 'services/user/user.service';
import { TicketAttributeService } from 'services/ticket-attribute/ticket-attribute.service';
import { GalleryTicketComponent } from '../gallery-ticket/gallery-ticket.component';
import { SoundListComponent } from '../sound-list/sound-list.component';
import TicketCategory from 'models/ticket-category/ticket-category.model';
import { TicketCategoryService } from 'services/ticket-category/ticket-category.service';
import { GeoCodeService } from 'services/geocode/geocode.service';
import moment from 'moment';
import ExternalFeature from 'models/external-feature/external-feature.model';

@Component({
  selector: 'app-list-ticket',
  templateUrl: './list-ticket.component.html',
  styleUrls: ['./list-ticket.component.scss'],
})
export class ListTicketComponent implements OnInit, OnDestroy {
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChildren(MatExpansionPanel) expansionPanels: MatExpansionPanel[];

  @Input() public project: Project = new Project();
  public page: PaginatedList<Ticket> = new PaginatedList();
  public user: User = new User();
  public projectPermission: ProjectPermission = new ProjectPermission();
  public selectedTickets: Ticket[] = [];
  public clickedTicket: ExternalFeature = new ExternalFeature();
  public status: TicketStatus;
  public statuses: TicketStatus[] = [];
  public attributes: TicketAttribute[];
  public searchableAttributes: TicketAttribute[];
  public customAttributes: { name: string, url: string, key: string }[] = []
  public selectedUser: User;
  public users: User[] = [];
  public interval: [number, number] = [null, null];
  public showBatchActions = false;
  public showMoreFilter = false;
  public currentPage = 'map';
  public categories: TicketCategory[] = [];
  public selectedCategory: TicketCategory = null;
  public selectedDistrict: number = null;
  public searchables = {};

  private userSubscription: Subscription;
  private ticketSubscription: Subscription;
  private permissionSubscription: Subscription;
  private clickedSubscription: Subscription;
  private selectedTicketSubscription: Subscription;

  public searchTerm: string = '';
  private buffered = () => { };
  private searchBuffered = () => { };
  public searchList: string[] = [];

  public isObject = false;
  public layerData = { objId: null, url: null };
  @Input() public refSubject = new BehaviorSubject<boolean>(false);

  constructor(
    public router: Router,
    public ticketService: TicketService,
    public ticketAttributeService: TicketAttributeService,
    public arcgisMapService: ArcgisMapService,
    public projectService: ProjectService,
    public userService: UserService,
    private dialog: MatDialog,
    private ticketCategoryService: TicketCategoryService,
    private geoCodeService: GeoCodeService,
  ) { }

  ngOnInit() {
    this.ticketAttributeService.list(this.project).then((attributes) => {
      this.attributes = attributes;
      const sAttributes = attributes.filter((att) => att.searchable);
      this.searchableAttributes = sAttributes;
      sAttributes.forEach((att) => att.isDate ? this.searchables[att.id] = { attributeId: att.id, from: null, to: null } : this.searchables[att.id] = { attributeId: att.id, value: null });
      this.attributes.forEach((att) => att.ticketAttributeExtensions.forEach((ext) => this.customAttributes.push({ name: att.name, url: ext.externalLayer.url, key: ext.argisAttributeName })));
    });

    this.refSubject.subscribe((bool) => {
      if (bool) {
        this.refresh();
      }
    })
    this.ticketCategoryService.list(this.project).then((categories) => this.categories = categories);
    this.clickedSubscription = this.arcgisMapService.clickedFeature.subscribe((feature) => {
      this.clickedTicket = feature ? feature : new ExternalFeature();
      if (this.expansionPanels) {
        this.expansionPanels.forEach((panel) => {
          if (panel._body.nativeElement.previousElementSibling.attributes.getNamedItem('data-id').value === this.clickedTicket.ticketId) {
            panel._body.nativeElement.parentElement.scrollIntoView({ block: 'end', inline: 'center' });
          }
        })
      }
      if (feature && feature.objectId) {
        this.layerData = { objId: feature.objectId, url: feature.externalLayer.url };
        this.ticketService.getByLayerAndObjectId(this.project.id, feature.objectId, feature.externalLayer.url);
        this.isObject = true;
      } else {
        if (this.isObject) {
          this.refresh();
        }
        this.isObject = false;
      }
    });

    this.user = this.userService.getCurrentUser().value;
    if (!this.user.username.includes('admin')) {
      this.selectedUser = this.user;
    }
    this.ticketService.statusList().then(async (statuses) => {
      this.statuses = statuses;
      this.status = statuses.find((status) => status.name === 'new');
      await this.ticketService.selectPage(
        this.project,
        1,
        1000,
        this.selectedUser,
        this.status,
        [Date.now() - (7 * 24 * 3600 * 1000), Date.now()],
        this.selectedCategory,
        this.searchTerm,
        this.selectedDistrict,
        this.searchables,
      );
      await this.projectService.userList(this.project).then((people) => this.users = people);
    });

    this.ticketSubscription = this.ticketService.getSelectedList().subscribe(async (list) => {
      this.page = list;
      if (this.selectedTicketSubscription) {
        this.selectedTicketSubscription.unsubscribe();
      }
      this.selectedTicketSubscription = this.ticketService.getSelectedInstance().subscribe((tickets) => {
        this.selectedTickets = tickets;
        tickets.length > 1
          ? this.showBatchActions = true
          : this.showBatchActions = false;
      });
    });
    this.permissionSubscription = this.projectService.getSelectedPermissions().subscribe((permission) => this.projectPermission = permission);
  }

  getCustomAttributes() {
    return this.customAttributes.map(cAtt => cAtt.url === this.clickedTicket.externalLayer.url
        ? ` | ${cAtt.name}: ${this.clickedTicket.other[cAtt.key]}`
        : false
    ).filter(v => v)
    .join('');
  }

  changeSearchable(att, value) {
    this.searchables[att].value = value;
    this.buffered();
    new Promise((resolve, reject) => {
      this.buffered = reject;
      setTimeout(resolve, 300);
    }).then(() => {
      this.switchToUltimate();
      this.refresh();
    })
      .catch(console.log);
  }

  searchableStart(att, start) {
    const current = this.searchables[att];
    if (start === null) {
      current.from = null;
      if (current.from === current.to) {
        this.refresh();
        this.switchToUltimate();
      }
      return;
    }
    current.from = start.getTime();
    if (current.from && current.to) {
      this.refresh();
      this.switchToUltimate();
    }
  }

  searchableEnd(att, end) {
    const current = this.searchables[att];
    if (end === null) {
      current.to = null;
      if (current.from === current.to) {
        this.refresh();
        this.switchToUltimate();
      }
      return;
    }
    current.to = end.getTime();
    if (current.from && current.to) {
      this.refresh();
      this.switchToUltimate();
    }
  }

  buffer() {
    this.buffered();
    new Promise((resolve, reject) => {
      this.buffered = reject;
      setTimeout(resolve, 300);
    }).then(() => {
      this.geoCodeService.getAddressHints(this.searchTerm).then((geos) => this.searchList = geos.map((geo) => geo.properties.address));
      this.searchByTerm();
    })
      .catch(console.log);
  }

  searchByTerm() {
    this.searchBuffered();
    new Promise((resolve, reject) => {
      this.searchBuffered = reject;
      setTimeout(resolve, 750);
    }).then(() => this.updateList())
      .catch(console.log);
  }

  checkSelection(id: string): boolean {
    return this.selectedTickets.some((ticket) => ticket.id === id);
  }

  selectStatus(status: TicketStatus) {
    this.status = status;
    this.switchToUltimate();
    this.refresh();
  }

  selectCategory(cat: TicketCategory) {
    this.selectedCategory = cat;
    this.switchToUltimate();
    this.refresh();
  }

  selectDistrict(district: number) {
    this.selectedDistrict = district;
    this.switchToUltimate();
    this.refresh();
  }

  selectUser(user: User) {
    this.selectedUser = user;
    this.switchToUltimate();
    this.refresh();
  }

  export() {
    this.ticketService.exportData(
      this.project,
      1,
      1000,
      this.selectedUser,
      this.status,
      this.interval,
      this.selectedCategory,
      this.searchTerm,
      this.selectedDistrict,
      this.searchables,
    ).subscribe((b) => {
      console.log(b)
      const a = document.createElement('a');
      const blob = new Blob([b], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
    
      a.href = url;
      a.download = 'myFile.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
  }

  itemValue(item) {
    const att = this.attributes.find((attr) => attr.id === item.ticketAttribute.id)
    const value = +item.value
    if (att && att.isDate && value !== 0) {
      return moment(value).format('YYYY-MM-DD');
    }
    return item.value;
  }

  selectStart(start: Date) {
    if (start === null) {
      this.interval[0] = null;
      if (this.interval[0] === this.interval[1]) {
        this.switchToUltimate();
        this.refresh();
      }
      return;
    }
    this.interval[0] = start.getTime();
    if (this.interval[0] && this.interval[1]) {
      this.switchToUltimate();
      this.refresh();
    }
  }

  selectEnd(end: Date) {
    if (end === null) {
      this.interval[1] = null;
      if (this.interval[0] === this.interval[1]) {
        this.switchToUltimate();
        this.refresh();
      }
      return;
    }
    this.interval[1] = end.getTime() + (((24 * 3600) - 1) * 1000);
    if (this.interval[0] && this.interval[1]) {
      this.switchToUltimate();
      this.refresh();
    }
  }

  setStatus(status: TicketStatus, ticket: Ticket) {
    this.ticketService.setStatus(ticket, status).then(() => this.updateList());
  }

  setStatusOnSelectedTickets = async (status: TicketStatus) => {
    for (let ticket of this.selectedTickets) {
      await this.ticketService.setStatus(ticket, status);
    }
    this.refresh();
  }

  updateList() {
    this.ticketService.selectPage(
      this.project,
      1,
      1000,
      this.selectedUser,
      this.status,
      this.interval,
      this.selectedCategory,
      this.searchTerm,
      this.selectedDistrict,
      this.searchables
    );
  }

  deleteTicket(ticket: Ticket) {
    this.ticketService.delete(ticket).then(() => this.refresh());
  }

  deleteSelectedTickets = async () => {
    for (let ticket of this.selectedTickets) {
      await this.ticketService.delete(ticket);
    }
    this.refresh();
  }

  clearSelectedTickets() {
    this.ticketService.getSelectedInstance().next([]);
  }

  create() {
    this.router.navigate(['/ticket/create'], { queryParams: { project: this.project.id } });
  }

  arcgisTickets() {
    this.router.navigate(['/ticket/esri'], { queryParams: { project: this.project.id, page: 1, limit: 100 } });
  }

  async select(item: Ticket) {
    await this.ticketService.select(item.id);
  }

  openEditModal = async (event: MouseEvent, ticket: Ticket) => {
    event.stopPropagation();

    if (!this.attributes) {
      await this.ticketAttributeService.list(this.project).then((attributes) => {
        this.attributes = attributes;
      });
    }

    const dialogRef = this.dialog.open(
      TicketAttributeCardComponent,
      {
        data: {
          ticket,
          statuses: this.statuses,
          attributeList: this.attributes,
          categories: this.categories,
        },
      },
    );
    dialogRef.afterClosed().subscribe((isSaved: boolean) => {
      if (isSaved) {
        this.refresh();
      }
    });
  }

  switchToUltimate() {
    this.isObject = false;
  }

  refresh() {
    if (this.isObject) {
      this.ticketService.getByLayerAndObjectId(this.project.id, this.layerData.objId, this.layerData.url);
    } else {
      this.ticketService.getSelectedInstance().next([]);
      this.updateList();
    }
  }

  gallery(ticket: Ticket) {
    let dialogRef = this.dialog.open(GalleryTicketComponent, {
      data: {
        ticket: ticket
      },
      maxHeight: '90vh',
    });
  }

  sounds(ticket: Ticket) {
    let dialogRef = this.dialog.open(SoundListComponent, {
      data: {
        ticket: ticket
      },
    });
  }

  ngOnDestroy() {
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
    }
    if (this.clickedSubscription) {
      this.clickedSubscription.unsubscribe();
    }
    if (this.selectedTicketSubscription) {
      this.selectedTicketSubscription.unsubscribe();
    }
  }
}
